<template>
    <div class="pb-border">
        <div v-if="hover" class="pb-face-toggle">
            <button type="button" :class="{ active: face === 'normal' }" @click="setFace('normal')">Normal</button>
            <button type="button" :class="{ active: face === 'hover' }" @click="setFace('hover')">Hover</button>
        </div>

        <!-- Linked: width + style + color + toggle -->
        <div v-if="isLinked" class="pb-border-main">
            <div class="pb-border-compact">
                <input type="number" min="0" class="pb-border-width" :value="singleBorder.width" @input="onSingleWidth($event)" placeholder="0" />
                <select class="pb-border-style" v-model="globalStyle" @change="onStyleChange">
                    <option value="none">None</option>
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                    <option value="double">Double</option>
                </select>
                <div class="pb-border-color" :class="{ 'not-set': !singleBorder.color }">
                    <input type="color" :value="singleBorder.color || '#000000'" @input="onColor('single', $event.target.value)" />
                </div>
            </div>
            <div class="pb-link-toggle">
                <button type="button" :class="{ active: isLinked }" title="All sides together" @click="setLinked(true)"><i class="fas fa-link"></i></button>
                <button type="button" :class="{ active: !isLinked }" title="Individual sides" @click="setLinked(false)"><i class="fas fa-unlink"></i></button>
            </div>
        </div>

        <!-- Unlinked: style + toggle, then per-side width/color -->
        <div v-else class="pb-border-unlinked">
            <div class="pb-border-header">
                <select class="pb-border-style" v-model="globalStyle" @change="onStyleChange">
                    <option value="none">None</option>
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                    <option value="double">Double</option>
                </select>
                <div class="pb-link-toggle">
                    <button type="button" :class="{ active: isLinked }" title="All sides together" @click="setLinked(true)"><i class="fas fa-link"></i></button>
                    <button type="button" :class="{ active: !isLinked }" title="Individual sides" @click="setLinked(false)"><i class="fas fa-unlink"></i></button>
                </div>
            </div>
            <div class="pb-border-grid">
                <div v-for="side in ['top', 'right', 'bottom', 'left']" :key="side" class="pb-border-side">
                    <div class="pb-side-label">{{ getSideLabel(side) }}</div>
                    <div class="pb-side-controls">
                        <input type="number" min="0" class="pb-border-width" :value="borders[side].width" @input="onSideWidth(side, $event)" placeholder="0" />
                        <div class="pb-border-color" :class="{ 'not-set': !borders[side].color }">
                            <input type="color" :value="borders[side].color || '#000000'" @input="onColor(side, $event.target.value)" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <small v-if="help" class="pb-help">{{ help }}</small>
    </div>
</template>

<script>
// Heygov-style border control: linked (one width/style/color) -> "1px solid #000000",
// or unlinked per-side -> JSON {top,right,bottom,left}. Native color swatch. When `hover`
// is true a Normal/Hover toggle edits the base value (`change`) or hover value (`changeHover`).
export default {
    name: "BorderControl",
    props: {
        value: { type: String, default: "" },
        hoverValue: { type: String, default: "" },
        hover: { type: Boolean, default: false },
        help: { type: String, default: "" },
    },
    emits: ["change", "changeHover"],
    data() {
        return {
            borders: {
                top: { width: 0, style: "solid", color: "#000000" },
                right: { width: 0, style: "solid", color: "#000000" },
                bottom: { width: 0, style: "solid", color: "#000000" },
                left: { width: 0, style: "solid", color: "#000000" },
            },
            singleBorder: { width: 0, style: "solid", color: "#000000" },
            isLinked: true,
            globalStyle: "solid",
            face: "normal",
        };
    },
    computed: {
        activeValue() {
            return this.face === "hover" ? this.hoverValue : this.value;
        },
    },
    created() {
        this.parseValue(this.activeValue);
    },
    watch: {
        value() {
            if (this.face === "normal") this.parseValue(this.value);
        },
        hoverValue() {
            if (this.face === "hover") this.parseValue(this.hoverValue);
        },
    },
    methods: {
        parseValue(value) {
            const reset = () => {
                this.borders = {
                    top: { width: 0, style: "solid", color: "#000000" },
                    right: { width: 0, style: "solid", color: "#000000" },
                    bottom: { width: 0, style: "solid", color: "#000000" },
                    left: { width: 0, style: "solid", color: "#000000" },
                };
                this.singleBorder = { width: 0, style: "solid", color: "#000000" };
                this.globalStyle = "solid";
                this.isLinked = true;
            };
            if (!value || value === "" || value === "none") {
                reset();
                return;
            }
            const single = value.match(/^(\d+)px\s+(\w+)\s+(#[0-9a-fA-F]{3,8})$/);
            if (single) {
                const b = { width: parseInt(single[1]) || 0, style: single[2] || "solid", color: single[3] || "#000000" };
                this.singleBorder = { ...b };
                this.globalStyle = b.style;
                this.borders = { top: { ...b }, right: { ...b }, bottom: { ...b }, left: { ...b } };
                this.isLinked = true;
                return;
            }
            try {
                const parsed = JSON.parse(value);
                if (parsed && (parsed.top || parsed.right || parsed.bottom || parsed.left)) {
                    ["top", "right", "bottom", "left"].forEach((side) => {
                        const m = String(parsed[side] || "0px solid #000000").match(/^(\d+)px\s+(\w+)\s+(#[0-9a-fA-F]{3,8})$/);
                        this.borders[side] = m ? { width: parseInt(m[1]) || 0, style: m[2] || "solid", color: m[3] || "#000000" } : { width: 0, style: "solid", color: "#000000" };
                    });
                    this.globalStyle = this.borders.top.style || "solid";
                    const allSame = ["right", "bottom", "left"].every((s) => this.borders[s].width === this.borders.top.width && this.borders[s].color === this.borders.top.color);
                    this.isLinked = allSame;
                    if (allSame) this.singleBorder = { ...this.borders.top };
                    return;
                }
            } catch (e) {
                /* not JSON */
            }
            reset();
        },
        onColor(which, color) {
            if (which === "single") {
                this.singleBorder.color = color;
                this.onSingleBorderChange();
            } else {
                this.borders[which].color = color;
                this.onBorderChange();
            }
        },
        // read width straight from the event (avoid the v-model/@input ordering race that
        // emitted a stale 0 and made the field snap back)
        onSingleWidth(e) {
            this.singleBorder.width = parseInt(e.target.value) || 0;
            this.onSingleBorderChange();
        },
        onSideWidth(side, e) {
            this.borders[side].width = parseInt(e.target.value) || 0;
            this.onBorderChange();
        },
        onStyleChange() {
            this.singleBorder.style = this.globalStyle;
            ["top", "right", "bottom", "left"].forEach((s) => (this.borders[s].style = this.globalStyle));
            this.isLinked ? this.onSingleBorderChange() : this.onBorderChange();
        },
        onSingleBorderChange() {
            ["top", "right", "bottom", "left"].forEach((s) => (this.borders[s] = { ...this.singleBorder }));
            this.emitValue();
        },
        onBorderChange() {
            this.emitValue();
        },
        setLinked(v) {
            if (this.isLinked === v) return;
            this.isLinked = v;
            if (v) {
                const first = this.borders.top.width > 0 ? this.borders.top : this.borders.right.width > 0 ? this.borders.right : this.borders.bottom.width > 0 ? this.borders.bottom : this.borders.left.width > 0 ? this.borders.left : { width: 0, style: this.globalStyle, color: "#000000" };
                this.singleBorder = { ...first };
                ["top", "right", "bottom", "left"].forEach((s) => (this.borders[s] = { ...first }));
                this.emitValue();
            }
        },
        setFace(face) {
            this.face = face;
            this.parseValue(this.activeValue);
        },
        getSideLabel(side) {
            return { top: "T", right: "R", bottom: "B", left: "L" }[side];
        },
        emitValue() {
            const event = this.face === "hover" ? "changeHover" : "change";
            if (this.isLinked) {
                if (!this.singleBorder.width || this.singleBorder.style === "none") {
                    this.$emit(event, "");
                } else {
                    this.$emit(event, `${this.singleBorder.width}px ${this.singleBorder.style} ${this.singleBorder.color}`);
                }
                return;
            }
            const obj = {};
            let any = false;
            ["top", "right", "bottom", "left"].forEach((side) => {
                if (this.borders[side].width > 0 && this.borders[side].style !== "none") {
                    obj[side] = `${this.borders[side].width}px ${this.borders[side].style} ${this.borders[side].color}`;
                    any = true;
                } else {
                    obj[side] = "";
                }
            });
            this.$emit(event, any ? JSON.stringify(obj) : "");
        },
    },
};
</script>

<style scoped>
.pb-border {
    width: 100%;
}
.pb-face-toggle {
    display: inline-flex;
    margin-bottom: 6px;
    border-radius: 8px;
    overflow: hidden;
    background: #f3f4f6;
}
.pb-face-toggle button {
    border: none;
    background: transparent;
    color: #6b7280;
    font-size: 11px;
    padding: 4px 12px;
    cursor: pointer;
}
.pb-face-toggle button.active {
    background: #fff;
    color: #333;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.pb-border-main {
    display: flex;
    align-items: center;
    gap: 10px;
}
.pb-border-compact {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
    background: #fff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 2px 4px;
    height: 31.5px;
}
.pb-border-width,
.pb-border-style {
    border: none;
    background: transparent;
    height: 27px;
    font-size: 13px;
    color: #333;
    outline: none;
    text-align: center;
    -moz-appearance: textfield;
}
.pb-border-width::-webkit-inner-spin-button,
.pb-border-width::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.pb-border-compact .pb-border-width {
    flex: 0.7;
    min-width: 0;
}
.pb-border-compact .pb-border-style {
    flex: 2;
    min-width: 0;
    text-align: left;
    padding: 0 6px;
}
.pb-border-color {
    position: relative;
    width: 27px;
    height: 27px;
    flex-shrink: 0;
}
.pb-border-color.not-set::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    width: 2px;
    height: 22px;
    background-color: #ef4444;
    pointer-events: none;
    z-index: 2;
}
.pb-border-color input[type="color"] {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    background: transparent;
    padding: 0;
}
.pb-border-color input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0;
}
.pb-border-color input[type="color"]::-webkit-color-swatch {
    border: 1px solid #e5e7eb;
    border-radius: 4px;
}
.pb-border-unlinked {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.pb-border-header {
    display: flex;
    align-items: center;
    gap: 10px;
}
.pb-border-header .pb-border-style {
    flex: 1;
    background: #fff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    height: 31.5px;
    text-align: left;
    padding: 0 8px;
}
.pb-border-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}
.pb-border-side {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.pb-side-label {
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
}
.pb-side-controls {
    display: flex;
    gap: 6px;
    align-items: center;
}
.pb-side-controls .pb-border-width {
    flex: 1;
    min-width: 0;
    background: #fff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    height: 31.5px;
}
.pb-side-controls .pb-border-color {
    width: 31.5px;
    height: 31.5px;
}
.pb-link-toggle {
    display: inline-flex;
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;
    background: #e7ebef;
}
.pb-link-toggle button {
    border: none;
    background: transparent;
    color: #9ca3af;
    width: 30px;
    height: 31.5px;
    cursor: pointer;
    font-size: 12px;
}
.pb-link-toggle button.active {
    background: #fff;
    color: #2563eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.pb-help {
    display: block;
    margin-top: 6px;
    font-size: 10px;
    color: #9ca3af;
}
</style>
